import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { IconUserPlus, IconDeviceImac, IconQuestionMark, IconFileCheck } from '@tabler/icons-react';

gsap.registerPlugin(ScrollTrigger);

const steps = [
    {
        icon: IconUserPlus,
        title: 'Sign Up',
        description: 'Register on our platform with a simple form and get started on your journey.',
        step: '01',
    },
    {
        icon: IconDeviceImac,
        title: 'Get Access',
        description: 'Access our comprehensive library of over 2000+ questions and study materials.',
        step: '02',
    },
    {
        icon: IconQuestionMark,
        title: 'Practice',
        description: 'Prepare for exams in multiple revision modes and track your progress.',
        step: '03',
    },
    {
        icon: IconFileCheck,
        title: 'Get Results',
        description: 'Compare your results with peers using advanced analytics and insights.',
        step: '04',
    },
];

export default function HowitWork() {
    const sectionRef = useRef(null);
    const titleRef = useRef(null);
    const cardsRef = useRef([]);
    const buttonRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(titleRef.current,
                { y: -30, opacity: 0 },
                {
                    y: 0, opacity: 1, duration: 0.7, ease: 'power3.out',
                    scrollTrigger: { trigger: sectionRef.current, start: 'top 80%', toggleActions: 'play none none reverse' },
                }
            );

            cardsRef.current.forEach((card, index) => {
                if (!card) return;
                gsap.fromTo(card,
                    { y: 50, opacity: 0 },
                    {
                        y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', delay: index * 0.12,
                        scrollTrigger: { trigger: sectionRef.current, start: 'top 70%', toggleActions: 'play none none reverse' },
                    }
                );
            });

            if (buttonRef.current) {
                gsap.fromTo(buttonRef.current,
                    { y: 20, opacity: 0 },
                    {
                        y: 0, opacity: 1, duration: 0.6, ease: 'power2.out',
                        scrollTrigger: { trigger: buttonRef.current, start: 'top 92%', toggleActions: 'play none none reverse' },
                    }
                );
            }
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} className="w-full bg-gray-50 py-12 sm:py-16 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="absolute top-20 right-0 w-80 h-80 bg-pumpkinToast-200/30 rounded-full blur-[130px]" />
            <div className="absolute bottom-10 left-0 w-64 h-64 bg-[#2a1273]/8 rounded-full blur-[120px]" />

            <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div ref={titleRef} className="text-center mb-16 opacity-0">
                    <span className="inline-flex items-center px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-pumpkinToast-500 bg-pumpkinToast-500/10 rounded-full border border-pumpkinToast-500/15 mb-4">
                        How It Works
                    </span>
                    <h2 className="font-AndoraModernSerif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2a1273] mb-4">
                        Your Learning Journey
                    </h2>
                    <p className="text-gray-500 max-w-2xl mx-auto text-base sm:text-lg">
                        Follow these simple steps to begin your journey of Islamic knowledge and growth.
                    </p>
                </div>

                {/* Steps grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        return (
                            <div
                                key={index}
                                ref={el => (cardsRef.current[index] = el)}
                                className="group relative bg-white rounded-2xl p-7 sm:p-8 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-400 opacity-0"
                            >
                                {/* Step number */}
                                <span className="absolute top-4 right-5 text-5xl font-bold text-gray-100 font-AndoraModernSerif group-hover:text-pumpkinToast-100 transition-colors">
                                    {step.step}
                                </span>

                                {/* Icon */}
                                <div className="w-14 h-14 bg-gradient-to-br from-pumpkinToast-400 to-pumpkinToast-500 rounded-xl flex items-center justify-center mb-6 shadow-md shadow-pumpkinToast-500/25 group-hover:scale-110 transition-transform duration-300">
                                    <Icon size={26} className="text-white" stroke={2} />
                                </div>

                                <h3 className="text-lg font-bold text-gray-900 mb-2 font-AndoraModernSerif">
                                    {step.title}
                                </h3>
                                <p className="text-gray-500 text-sm leading-relaxed">
                                    {step.description}
                                </p>

                                {/* Bottom accent line */}
                                <div className="absolute bottom-0 left-8 right-8 h-0.5 bg-gradient-to-r from-transparent via-pumpkinToast-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
                            </div>
                        );
                    })}
                </div>

                {/* CTA */}
                <div className="text-center mt-14">
                    <button
                        ref={buttonRef}
                        className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#2a1273] hover:bg-[#351693] text-white font-semibold rounded-xl shadow-lg shadow-[#2a1273]/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl opacity-0"
                    >
                        Start Now
                    </button>
                </div>
            </div>
        </section>
    );
}
